import * as React from 'react';
import { ChevronLeft, ChevronLeftIcon, ChevronRight, ChevronRightIcon, ChevronsLeft, ChevronsRight, MoreHorizontalIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
  return <nav role="navigation" aria-label="pagination" data-slot="pagination" className={cn('mx-auto flex w-full justify-center', className)} {...props} />;
}

function PaginationContent({ className, ...props }: React.ComponentProps<'ul'>) {
  return <ul data-slot="pagination-content" className={cn('flex flex-row items-center gap-1', className)} {...props} />;
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
  return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
  React.ComponentProps<'button'>;

function PaginationLink({ className, isActive, size = 'icon', ...props }: PaginationLinkProps) {
  return (
    <Button
      aria-current={isActive ? 'page' : undefined}
      data-slot="pagination-link"
      variant={isActive ? 'default' : 'ghost-dark'}
      className={cn(className)}
      {...props}
    />
  );
}

function PaginationPrevious({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink aria-label="Go to previous page" size="default" className={cn('gap-1 px-2.5 sm:pl-2.5', className)} {...props}>
      <ChevronLeftIcon />
      <span className="hidden sm:block">Previous</span>
    </PaginationLink>
  );
}

function PaginationNext({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink aria-label="Go to next page" size="default" className={cn('gap-1 px-2.5 sm:pr-2.5', className)} {...props}>
      <span className="hidden sm:block">Next</span>
      <ChevronRightIcon />
    </PaginationLink>
  );
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span aria-hidden data-slot="pagination-ellipsis" className={cn('flex size-9 items-center justify-center', className)} {...props}>
      <MoreHorizontalIcon className="size-4" />
      <span className="sr-only">More pages</span>
    </span>
  );
}

type AnimatedPaginationProps = {
  actualPage: number;
  setPage: (page: number) => void;
  totalPages: number;
};

export default function AnimatedPagination({
  //TODO need to refactor this to use the new pagination component
  actualPage,
  setPage,
  totalPages,
}: AnimatedPaginationProps) {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [highlightStyle, setHighlightStyle] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldPop, setShouldPop] = useState(false);
  const [visualPage, setVisualPage] = useState(actualPage);

  useEffect(() => {
    if (!isAnimating) setVisualPage(actualPage);
  }, [actualPage, isAnimating]);

  const pages = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i);
    if (visualPage <= 2) return [0, 1, 2, 3, 4];
    if (visualPage >= totalPages - 3) return [totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1];
    return [visualPage - 2, visualPage - 1, visualPage, visualPage + 1, visualPage + 2];
  }, [visualPage, totalPages]);

  useEffect(() => {
    const idx = pages.indexOf(visualPage);
    const node = buttonRefs.current[idx];
    if (node) {
      const rect = node.getBoundingClientRect();
      const parentNode = node.parentNode;
      if (parentNode && parentNode instanceof Element) {
        const parentRect = parentNode.getBoundingClientRect();
        setHighlightStyle({
          left: rect.left - parentRect.left,
          width: rect.width,
        });
      }
    }
  }, [visualPage, pages]);

  const handlePageClick = (page: number) => {
    if (isAnimating || page === actualPage) return;
    setIsAnimating(true);
    setVisualPage(page);
    setShouldPop(true);
    setTimeout(() => {
      setIsAnimating(false);
      setPage(page);
    }, 120);
  };

  useEffect(() => {
    if (shouldPop) {
      const timer = setTimeout(() => setShouldPop(false), 100);
      return () => clearTimeout(timer);
    }
  }, [shouldPop]);

  const prevDisabled = isAnimating || actualPage === 0;
  const nextDisabled = isAnimating || actualPage === totalPages - 1;

  return (
    <div className="relative flex gap-1 min-h-[40px] py-1">
      {totalPages > 1 && (
        <motion.div
          className="absolute top-1 bg-primary rounded-2xl z-0 h-10"
          animate={{
            left: highlightStyle.left,
            width: highlightStyle.width,
            scale: shouldPop ? 1.13 : 1,
          }}
          transition={{
            left: { duration: 0.1, ease: 'easeInOut' },
            width: { duration: 0.1, ease: 'easeInOut' },
            scale: { stiffness: 700, damping: 13, duration: 0.05 },
          }}
        />
      )}
      {totalPages > 1 && (
        <Button variant="ghost-dark" onClick={() => !prevDisabled && handlePageClick(actualPage - 1)} disabled={prevDisabled}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
      )}
      {pages[0] > 0 && (
        <Button variant="ghost-dark" onClick={() => handlePageClick(0)} disabled={isAnimating}>
          <ChevronsLeft className="w-5 h-5" />
        </Button>
      )}
      {pages.map((page, idx) => {
        if (idx === 0 && page > 0) return null;
        if (idx === pages.length - 1 && page < totalPages - 1) return null;
        return (
          <Button
            key={page}
            ref={(el) => {
              buttonRefs.current[idx] = el;
            }}
            className='relative'
            variant={page === visualPage ? 'default' : 'ghost-dark'}
            onClick={() => handlePageClick(page)}
            disabled={isAnimating}
          >
            {page + 1}
          </Button>
        );
      })}
      {pages[pages.length - 1] < totalPages - 1 && (
        <Button variant="ghost-dark" onClick={() => handlePageClick(totalPages - 1)} disabled={isAnimating}>
          <ChevronsRight className="w-5 h-5" />
        </Button>
      )}
      {totalPages > 1 && (
        <Button variant="ghost-dark" onClick={() => !nextDisabled && handlePageClick(actualPage + 1)} disabled={nextDisabled}>
          <ChevronRight className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
}

export { Pagination, PaginationContent, PaginationLink, PaginationItem, PaginationPrevious, PaginationNext, PaginationEllipsis, AnimatedPagination };
